import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { jsPDF } from "jspdf";

const PdfExportContext = createContext(null);
const PdfExportSectionContext = createContext(null);

function formatTitle(value, fallback = "dashboard") {
  if (!value || typeof value !== "string") {
    return fallback;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

export function PdfExportProvider({ children, documentTitle = "Dashboard" }) {
  const exportablesRef = useRef([]);
  const defaultTitle = formatTitle(documentTitle, "dashboard");

  const registerExportable = useCallback((entry) => {
    if (!entry || typeof entry.getContent !== "function") {
      return () => {};
    }

    const id = `pdf-${Math.random().toString(16).slice(2)}`;
    exportablesRef.current.push({ id, ...entry });

    return () => {
      exportablesRef.current = exportablesRef.current.filter((item) => item.id !== id);
    };
  }, []);

  const exportPdf = useCallback(
    async ({ title, metaLines } = {}) => {
      const pdfTitle = formatTitle(title, defaultTitle);
      const page = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
        compress: true,
      });

      const pageWidth = page.internal.pageSize.getWidth();
      const pageHeight = page.internal.pageSize.getHeight();
      const margin = 28;
      let cursorY = margin;

      // Header
      page.setFontSize(16);
      page.text(pdfTitle, margin, cursorY);
      cursorY += 18;

      if (Array.isArray(metaLines) && metaLines.length > 0) {
        page.setFontSize(10);
        metaLines.forEach((line) => {
          const safeLine = typeof line === "string" ? line : "";
          if (safeLine.trim().length > 0) {
            page.text(safeLine, margin, cursorY);
            cursorY += 12;
          }
        });
        cursorY += 6;
      }

      const items = [...exportablesRef.current];

      if (items.length === 0) {
        page.setFontSize(12);
        page.text("Geen grafieken om te exporteren", margin, cursorY);
        page.save(`${pdfTitle.replace(/\s+/g, "-").toLowerCase()}.pdf`);
        return;
      }

      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const content = await item.getContent();
        if (!content || !content.image?.src) {
          // Skip items that are not ready
          // eslint-disable-next-line no-continue
          continue;
        }

        const chartTitle = formatTitle(content.title, "");
        const availableWidth = pageWidth - margin * 2;
        const ratio = Math.min(availableWidth / content.image.width, 1);
        const targetWidth = content.image.width * ratio;
        const targetHeight = content.image.height * ratio;
        const requiredHeight = targetHeight + (chartTitle ? 26 : 12);

        if (cursorY + requiredHeight > pageHeight) {
          page.addPage();
          cursorY = margin;
        }

        if (chartTitle) {
          page.setFontSize(12);
          page.text(chartTitle, margin, cursorY);
          cursorY += 16;
        }

        page.addImage(content.image.src, "PNG", margin, cursorY, targetWidth, targetHeight);
        cursorY += targetHeight + 10;
      }

      page.save(`${pdfTitle.replace(/\s+/g, "-").toLowerCase()}.pdf`);
    },
    [defaultTitle]
  );

  const exportSectionedPdf = useCallback(
    async ({ title, metaLines = [], sections = [] } = {}) => {
      const pdfTitle = formatTitle(title, defaultTitle);
      const page = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: "a4",
        compress: true,
      });
      const pageWidth = page.internal.pageSize.getWidth();
      const pageHeight = page.internal.pageSize.getHeight();
      const margin = 24;
      const contentWidth = pageWidth - margin * 2;
      const bottom = pageHeight - margin;
      let hasPageContent = false;

      const drawHeader = (sectionLabel, continuation = false) => {
        page.setFillColor(105, 65, 198);
        page.rect(0, 0, pageWidth, 7, "F");
        page.setTextColor(52, 64, 84);
        page.setFont("helvetica", "bold");
        page.setFontSize(15);
        const titleLines = page.splitTextToSize(
          String(sectionLabel || "Vraag"),
          contentWidth - 80
        );
        page.text(titleLines, margin, 27);
        const metaY = 27 + titleLines.length * 13;
        page.setFont("helvetica", "normal");
        page.setFontSize(9);
        page.setTextColor(102, 112, 133);
        page.text(pdfTitle, margin, metaY);
        metaLines.filter(Boolean).forEach((line, index) => {
          page.text(String(line), margin, metaY + 12 + index * 11, {
            maxWidth: contentWidth - 80,
          });
        });
        if (continuation) {
          page.text("vervolg", pageWidth - margin, 27, { align: "right" });
        }
        const headerBottom =
          metaY + 20 + Math.max(0, metaLines.filter(Boolean).length - 1) * 11;
        page.setDrawColor(228, 231, 236);
        page.line(margin, headerBottom, pageWidth - margin, headerBottom);
        return headerBottom + 14;
      };

      const addSectionPage = (sectionLabel, continuation = false) => {
        if (hasPageContent) {
          page.addPage("a4", "landscape");
        }
        hasPageContent = true;
        return drawHeader(sectionLabel, continuation);
      };

      const drawSummary = (summary, sectionLabel, startY) => {
        if (!summary?.trim()) return startY;

        page.setFont("helvetica", "normal");
        page.setFontSize(10);
        const lines = page.splitTextToSize(summary.trim(), contentWidth - 28);
        const boxHeight = 32 + lines.length * 12;
        let cursorY = startY;
        if (cursorY + boxHeight > bottom) {
          cursorY = addSectionPage(sectionLabel, true);
        }

        page.setFillColor(249, 250, 251);
        page.rect(margin, cursorY, contentWidth, boxHeight, "F");
        page.setFillColor(127, 86, 217);
        page.rect(margin, cursorY, 3, boxHeight, "F");
        page.setTextColor(52, 64, 84);
        page.setFont("helvetica", "bold");
        page.setFontSize(10);
        page.text("Samenvatting", margin + 12, cursorY + 15);
        page.setFont("helvetica", "normal");
        page.text(lines, margin + 12, cursorY + 31);
        return cursorY + boxHeight + 14;
      };

      const drawTable = (content, sectionLabel, startY) => {
        let cursorY = startY;
        page.setFont("helvetica", "bold");
        page.setFontSize(11);
        page.setTextColor(52, 64, 84);
        page.text(content.title || "Tekstantwoorden", margin, cursorY);
        cursorY += 13;

        if (!content.rows?.length) {
          page.setFont("helvetica", "italic");
          page.setFontSize(9);
          page.setTextColor(102, 112, 133);
          page.text("Nog geen bewerkte tekstantwoorden beschikbaar.", margin, cursorY);
          return cursorY + 14;
        }

        content.rows.forEach((row, index) => {
          const lines = page.splitTextToSize(String(row.text || ""), contentWidth - 135);
          const rowHeight = Math.max(18, lines.length * 10 + 6);
          if (cursorY + rowHeight > bottom) {
            cursorY = addSectionPage(sectionLabel, true);
          }
          if (index % 2 === 0) {
            page.setFillColor(249, 250, 251);
            page.rect(margin, cursorY, contentWidth, rowHeight, "F");
          }
          page.setFont("helvetica", "normal");
          page.setFontSize(8.5);
          page.setTextColor(102, 112, 133);
          page.text(String(row.period || ""), margin + 5, cursorY + 12, {
            maxWidth: 75,
          });
          page.setTextColor(52, 64, 84);
          page.text(lines, margin + 84, cursorY + 12);
          page.setFont("helvetica", "bold");
          page.setTextColor(105, 65, 198);
          page.text(String(row.count || 0), pageWidth - margin - 5, cursorY + 12, {
            align: "right",
          });
          cursorY += rowHeight;
        });
        return cursorY + 10;
      };

      for (const section of sections) {
        let cursorY = addSectionPage(section.label);
        cursorY = drawSummary(section.summary, section.label, cursorY);
        const entries = exportablesRef.current
          .filter((entry) => entry.sectionKey === section.key)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        for (const [index, entry] of entries.entries()) {
          // eslint-disable-next-line no-await-in-loop
          const content = await entry.getContent();
          if (!content) {
            // eslint-disable-next-line no-continue
            continue;
          }
          if (content.kind === "table") {
            cursorY = drawTable(content, section.label, cursorY);
            // eslint-disable-next-line no-continue
            continue;
          }
          if (!content.image?.src) {
            // eslint-disable-next-line no-continue
            continue;
          }

          const availableHeight = bottom - cursorY;
          let ratio = Math.min(contentWidth / content.image.width, 1);
          let targetWidth = content.image.width * ratio;
          let targetHeight = content.image.height * ratio;
          if (targetHeight > availableHeight && cursorY > 100 && (index > 0 || !section.summary)) {
            cursorY = addSectionPage(section.label, true);
          }
          const fullPageHeight = bottom - cursorY;
          if (targetHeight > fullPageHeight) {
            ratio *= fullPageHeight / targetHeight;
            targetWidth = content.image.width * ratio;
            targetHeight = content.image.height * ratio;
          }
          const x = margin + Math.max(0, (contentWidth - targetWidth) / 2);
          page.addImage(content.image.src, "PNG", x, cursorY, targetWidth, targetHeight);
          cursorY += targetHeight + 10;
        }
      }

      if (!hasPageContent) {
        page.setFontSize(12);
        page.text("Geen onderdelen om te exporteren", margin, margin);
      }
      page.save(`${pdfTitle.replace(/\s+/g, "-").toLowerCase()}.pdf`);
    },
    [defaultTitle]
  );

  const value = useMemo(
    () => ({
      registerExportable,
      exportPdf,
      exportSectionedPdf,
    }),
    [exportPdf, exportSectionedPdf, registerExportable]
  );

  return <PdfExportContext.Provider value={value}>{children}</PdfExportContext.Provider>;
}

export function usePdfExport() {
  return useContext(PdfExportContext);
}

export function PdfExportSection({ sectionKey, order = 0, children }) {
  const value = useMemo(() => ({ sectionKey, order }), [order, sectionKey]);
  return (
    <PdfExportSectionContext.Provider value={value}>
      {children}
    </PdfExportSectionContext.Provider>
  );
}

export function usePdfExportSection() {
  return useContext(PdfExportSectionContext);
}
